import { useCallback, useEffect, useState } from 'react';

// Client-side favorites for gaming machines. The Machine API has no favorite
// concept, so we persist locally and keep every mounted component in sync via a
// tiny module-level pub/sub (survives across tabs through the storage event).

const STORAGE_KEY = 'casino_favorite_machines';

const read = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
};

let current: string[] = read();
const listeners = new Set<(ids: string[]) => void>();

const persist = (ids: string[]) => {
  current = ids;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
  listeners.forEach((fn) => fn(ids));
};

export const useFavorites = () => {
  const [favorites, setFavorites] = useState<string[]>(current);

  useEffect(() => {
    const listener = (ids: string[]) => setFavorites(ids);
    listeners.add(listener);

    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        current = read();
        setFavorites(current);
      }
    };
    window.addEventListener('storage', onStorage);

    // Sync with any changes that happened before this effect ran.
    setFavorites(current);

    return () => {
      listeners.delete(listener);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const isFavorite = useCallback((id?: string) => !!id && favorites.includes(id), [favorites]);

  const toggleFavorite = useCallback((id?: string) => {
    if (!id) return;
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    persist(next);
  }, []);

  return { favorites, isFavorite, toggleFavorite };
};
